import { translate } from "app/filters/i18nFilter";
import PanelSubmitIconButton from "app/pages/sales-intelligence/sub-modules/table-actions-panel/components/PanelSubmitIconButton/PanelSubmitIconButton";
import type SelectionDropdown from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown/SelectionDropdown";

import BaseActivatedPanel from "../../../table-actions-panel/components/BaseActivatedPanel/BaseActivatedPanel";
import { StyledPanelInner } from "../../../table-actions-panel/components/BaseActivatedPanel/styles";
import type { BaseActivatedPanelProps } from "../../../table-actions-panel/components/BaseActivatedPanel/types";
import PanelTitleWithIcon from "../../../table-actions-panel/components/PanelTitleWithIcon/PanelTitleWithIcon";
import SelectionDDItem from "../../../table-actions-panel/components/SelectionDDItem/SelectionDDItem";
import SelectionDropdownView from "../../../table-actions-panel/components/SelectionDropdown/SelectionDropdown";
import useActionsPanelState from "../../../table-actions-panel/hooks/useActionsPanelState";
import ManualSelectionItem from "../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import type { SelectionDropdownItem } from "../../../table-actions-panel/models/selection-dropdown-item/types";
import type { AssignableUser } from "../../types/common";
import UsersDropdown from "../UsersDropdown/UsersDropdown";

type Props = {
    isDisabled?: boolean;
    isLoading: boolean;
    numberOfResults: number;
    numberOfSelectedItems: number;
    users: readonly AssignableUser[];
    selectionDropdown: SelectionDropdown;
    onAssignSubmit: (user: AssignableUser, selectionItem: SelectionDropdownItem) => void | Promise<void>;
    onSelectionChange: (item: SelectionDropdownItem) => void;
} & BaseActivatedPanelProps;

const AssignToOtherUsersPanelView = (props: Props) => {
    const {
        isDisabled = false,
        isLoading,
        numberOfResults,
        numberOfSelectedItems,
        selectionDropdown,
        onAssignSubmit,
        onSelectionChange,
    } = props;
    const state = useActionsPanelState(selectionDropdown, {
        numberOfResults,
        numberOfSelectedItems,
        onSelectionChange,
    });

    const handleUserSelect = (user: AssignableUser): void => {
        void onAssignSubmit(user, state.activeSelectionItem);
    };

    return (
        <BaseActivatedPanel onDeactivate={props.onDeactivate}>
            <StyledPanelInner>
                <PanelTitleWithIcon
                    iconName="assign-to-user"
                    title={{ key: "si.multi_selector.assign.title" }}
                />
                <SelectionDropdownView
                    buttonText={state.activeSelectionItem.getSelectedTranslation()}
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                    onSelect={state.setActiveSelectionItem}
                >
                    {state.dropdownItems
                        .map((item) => (
                            <SelectionDDItem
                                key={item.id}
                                item={item}
                                isSelected={state.activeSelectionItem.id === item.id}
                            />
                        ))
                        .concat(
                            <SelectionDDItem
                                item={state.manualSelectionItem}
                                key={ManualSelectionItem.ID}
                                isSelected={state.activeSelectionItem.id === ManualSelectionItem.ID}
                            />,
                        )}
                </SelectionDropdownView>
                <UsersDropdown
                    button={
                        <PanelSubmitIconButton
                            isDisabled={state.isSelectionEmpty || isDisabled}
                            isLoading={isLoading}
                            dataAutomation="panel-submit-button"
                            text={translate("si.multi_selector.assign.btn.select_user")}
                            tooltipContent={translate("si.multi_selector.assign.tooltip.unselected_domains")}
                        />
                    }
                    numberOfLeads={state.activeSelectionItem.value}
                    users={props.users}
                    onSelect={handleUserSelect}
                    disabled={state.isSelectionEmpty || isLoading}
                />
            </StyledPanelInner>
        </BaseActivatedPanel>
    );
};

export default AssignToOtherUsersPanelView;
